import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { attractions } from '../data/mock';

export const Attractions = ({ onBookNow }) => {
  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-page via-card to-accent relative">
        <div className="absolute inset-0 cyber-grid opacity-20"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h1 className="heading-1 text-6xl md:text-7xl lg:text-8xl font-black uppercase mb-6 leading-tight">
            <span className="text-primary" style={{ textShadow: '0 0 20px var(--neon-green)' }}>Attr</span>
            <span className="text-blue" style={{ textShadow: '0 0 20px var(--neon-blue)' }}>acti</span>
            <span className="text-pink" style={{ textShadow: '0 0 20px var(--neon-pink)' }}>ons</span>
          </h1>
          <p className="body-large text-white max-w-3xl mx-auto">
            Eight unforgettable experiences. One immersive destination. Pick your adventure — pricing and scheduling handled right at checkout.
          </p>
        </div>
      </section>

      {/* Attractions */}
      <section className="py-16 bg-page relative">
        <div className="absolute inset-0 cyber-grid opacity-10"></div>
        <div className="container mx-auto px-6 space-y-16 relative z-10">
          {attractions.map((attraction, index) => {
            const IconComponent = LucideIcons[attraction.icon] || LucideIcons.Zap;
            const isEven = index % 2 === 0;
            const borderColors = ['border-green', 'border-blue', 'border-pink'];
            const glowColors = ['rgba(0, 255, 65, 0.3)', 'rgba(0, 217, 255, 0.3)', 'rgba(255, 0, 255, 0.3)'];
            const borderColor = borderColors[index % 3];
            const glowColor = glowColors[index % 3];

            return (
              <div
                key={attraction.id}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className={`bg-card border-2 ${borderColor} rounded-2xl h-96 flex items-center justify-center relative overflow-hidden group`} style={{ boxShadow: `0 0 20px ${glowColor}` }}>
                    {attraction.photo ? (
                      <>
                        <img
                          src={attraction.photo}
                          alt={attraction.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-page/50 via-transparent to-transparent pointer-events-none"></div>
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-pink/5 group-hover:opacity-70 transition-opacity pointer-events-none"></div>
                      </>
                    ) : attraction.images && attraction.images.length > 0 ? (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-br from-page to-card"></div>
                        <div className="relative z-10 grid grid-cols-2 gap-3 p-6 w-full h-full">
                          {attraction.images.slice(0, 4).map((imgSrc, imgIdx) => (
                            <div key={imgIdx} className="rounded-lg flex items-center justify-center overflow-hidden">
                              <img
                                src={imgSrc}
                                alt={`${attraction.name} logo ${imgIdx + 1}`}
                                className="w-full h-full object-contain p-3"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-pink/5 group-hover:opacity-70 transition-opacity pointer-events-none"></div>
                      </>
                    ) : attraction.image ? (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-br from-page to-card"></div>
                        <img 
                          src={attraction.image} 
                          alt={attraction.name}
                          className="relative z-10 w-full h-full object-contain p-8"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-pink/5 group-hover:opacity-70 transition-opacity pointer-events-none"></div>
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-blue/5 to-pink/5"></div>
                        <IconComponent className="text-primary opacity-20 group-hover:opacity-30 transition-opacity" size={120} style={{ filter: 'drop-shadow(0 0 20px var(--neon-green))' }} />
                      </>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-br from-primary to-blue rounded-full flex items-center justify-center`} style={{ boxShadow: '0 0 20px rgba(0, 255, 65, 0.5)' }}>
                      <IconComponent className="text-inverse" size={28} />
                    </div>
                    <div>
                      <h2 className="heading-3 text-3xl md:text-4xl font-black uppercase leading-tight">
                        <span className="text-primary" style={{ textShadow: '0 0 10px var(--neon-green)' }}>{attraction.name}</span>
                      </h2>
                      <p className="text-pink italic text-lg font-semibold">{attraction.tagline}</p>
                    </div>
                  </div>

                  <p className="body-medium text-white mb-6 leading-relaxed">
                    {attraction.description}
                  </p>

                  <div className={`bg-card border-2 ${borderColor} rounded-xl p-6 mb-6`} style={{ boxShadow: `0 0 15px ${glowColor}` }}>
                    <h3 className="caption text-blue mb-3">Features</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {attraction.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-secondary text-sm">
                          <span className="text-primary mt-1">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    onClick={onBookNow}
                    className="btn-primary font-bold"
                    data-testid={`attraction-book-${attraction.slug || attraction.id}`}
                  >
                    Book Now
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};